import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import CONFIG from "../../config/config";
import { Card, CardBody, CardFooter, CardHeader } from "../../components/card/card";
import { getStatusBadgeColor } from "../../misc.js";

// Fetch Xvids with applied filters and pagination
const fetchXvids = async (page, limit, filters = {}) => {
  const token = localStorage.getItem("token");
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  // Append filters if they are provided
  if (filters.status) {
    queryParams.append("status", filters.status);
  }
  if (filters.xvidType) {
    queryParams.append("xvidType", filters.xvidType);
  }

  const response = await fetch(
    `${CONFIG.API_URL}/xvid/list?${queryParams.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.json();
};

const XvidList = () => {
  const [xvids, setXvids] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();

  // Two sets of filter states:
  const [filterInputs, setFilterInputs] = useState({
    status: "",
    xvidType: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    status: "",
    xvidType: "",
  });

  // Load Xvids based on the current page, limit, and applied filters.
  const loadXvids = async () => {
    try {
      setIsLoading(true);
      const data = await fetchXvids(page, limit, appliedFilters);

      // Handle empty page after deletion (or if filter causes no items on current page)
      if (data.documents.length === 0 && page > 1) {
        setPage(page - 1);
        return;
      }

      setXvids(data.documents);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error("Error fetching xvids:", error);
      setError("Failed to load xvids. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Update xvids whenever page, limit, or appliedFilters change.
  useEffect(() => {
    loadXvids();
  }, [page, limit, appliedFilters]);

  const handlePreviousPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const handleEditXvid = (id) => navigate(`/create-xvid/${id}`);

  // Delete handler with token attached in the header.
  const handleDeleteXvid = async (id) => {
    if (window.confirm("Are you sure you want to delete this xvid?")) {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${CONFIG.API_URL}/xvid/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) throw new Error("Delete failed");
        await loadXvids(); // Refresh the list after deletion
      } catch (error) {
        console.error("Delete error:", error);
        setError("Failed to delete xvid. Please try again.");
      }
    }
  };

  // Handlers for applying and clearing filters.
  const handleApplyFilters = () => {
    setAppliedFilters(filterInputs);
    setPage(1); // Reset to page 1 when filters change.
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      status: "",
      xvidType: "",
    };
    setFilterInputs(clearedFilters);
    setAppliedFilters(clearedFilters);
    setPage(1); // Reset to page 1.
  };

  if (isLoading) return <div className="text-center mt-5">Loading...</div>;
  if (error) return <div className="alert alert-danger mt-5">{error}</div>;

  return (
    <Card>
      <CardHeader>
        <h1>Xvid List</h1>
      </CardHeader>
      <CardBody>
        {/* Filters Section */}
        <div className="mb-4">
          <div className="row g-2 align-items-end">
            <div className="col-md-2">
              <label htmlFor="filterStatus" className="form-label">
                Status
              </label>
              <select
                id="filterStatus"
                className="form-select"
                value={filterInputs.status}
                onChange={(e) =>
                  setFilterInputs({
                    ...filterInputs,
                    status: e.target.value,
                  })
                }
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="pending">Pending</option>
                <option value="created">Created</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="col-md-2">
              <label htmlFor="filterXvidType" className="form-label">
                Xvid Type
              </label>
              <input
                type="text"
                id="filterXvidType"
                className="form-control"
                placeholder="Filter by Xvid Type"
                value={filterInputs.xvidType}
                onChange={(e) =>
                  setFilterInputs({
                    ...filterInputs,
                    xvidType: e.target.value,
                  })
                }
              />
            </div>
            <div className="col-md-2">
              <button
                className="btn btn-primary"
                onClick={handleApplyFilters}
              >
                Apply Filters
              </button>
              <button
                className="btn btn-secondary ms-2"
                onClick={handleClearFilters}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Xvid Table */}
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Position</th>
              <th>Type</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {xvids.map((xvid, index) => (
              <tr key={xvid._id}>
                <td>{(page - 1) * limit + index + 1}</td>
                <td>{xvid.type}</td>
                <td>
                  <span className={`badge ${getStatusBadgeColor(xvid.status)}`}>
                    {xvid.status}
                  </span>
                </td>
                <td>
                  <button
                    className="btn btn-link p-0"
                    onClick={() => handleEditXvid(xvid._id)}
                    title="Edit"
                  >
                    <i className="fas fa-pencil-alt" />
                  </button>
                  <button
                    className="btn btn-link p-0 ms-2 text-danger"
                    onClick={() => handleDeleteXvid(xvid._id)}
                    title="Delete"
                  >
                    <i className="fas fa-trash-alt" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardBody>
      <CardFooter>
        {/* Pagination Controls */}
        <div className="d-flex justify-content-between align-items-center">
          <button
            className="btn btn-warning"
            onClick={handlePreviousPage}
            disabled={page === 1}
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            className="btn btn-success"
            onClick={handleNextPage}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default XvidList;
